<template>
  <v-container fluid :class="{ 'pa-2': $vuetify.breakpoint.smAndDown }">
    <!-- KPI : 3 cartes -->
    <v-row class="mb-2" :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }">
      <!-- KPI 1 : Véhicules -->
      <v-col cols="12" sm="6" md="4">
        <div
          class="kpi-card bgTable kpi-clickable"
          :class="{ 'kpi-card-mobile': $vuetify.breakpoint.xs }"
          @click="$router.push({ name: 'CompanyVehicles' })"
        >
          <div class="kpi-left">
            <div class="kpi-title" :class="{ 'kpi-title-mobile': $vuetify.breakpoint.xs }">
              {{ $t("Total véhicules") }}
            </div>
            <div class="kpi-value" :class="{ 'kpi-value-mobile': $vuetify.breakpoint.xs }">
              {{ (vehicles.total || 0).toLocaleString("fr-FR") }}
            </div>
            <div class="kpi-sub">
              {{ $t("Actifs") }}: {{ (vehicles.active || 0).toLocaleString("fr-FR") }} ·<br />
              {{ $t("Bloqués") }}: {{ (vehicles.blocked || 0).toLocaleString("fr-FR") }}
            </div>
          </div>
          <div class="kpi-right bgTable" :class="{ 'kpi-right-mobile': $vuetify.breakpoint.xs }">
            <div class="donut-wrap" :class="{ 'donut-wrap-mobile': $vuetify.breakpoint.xs }">
              <apexchart
                :key="'donut-vehicles-' + chartKey"
                type="donut"
                :height="$vuetify.breakpoint.xs ? 80 : 110"
                :options="vehiclesOptions"
                :series="vehicles.series"
              />
              <div class="donut-center" :class="{ 'donut-center-mobile': $vuetify.breakpoint.xs }">
                {{ centerLabel("vehicles", vehicles.total) }}
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- KPI 2 : Crédit carburant -->
      <v-col cols="12" sm="6" md="4">
        <div
          class="kpi-card bgTable kpi-clickable"
          :class="{ 'kpi-card-mobile': $vuetify.breakpoint.xs }"
          @click="$router.push({ name: 'CompanyVouchers' })"
        >
          <div class="kpi-left">
            <div class="kpi-title" :class="{ 'kpi-title-mobile': $vuetify.breakpoint.xs }">
              {{ $t("Crédit carburant") }}
            </div>
            <div class="kpi-value" :class="{ 'kpi-value-mobile': $vuetify.breakpoint.xs }">
              {{ (vouchers.total || 0).toLocaleString("fr-FR") }}
            </div>
            <div class="kpi-sub">
              {{ $t("En cours") }}: {{ (vouchers.in_use || 0).toLocaleString("fr-FR") }} ·<br />
              {{ $t("Non utilisé") }}: {{ (vouchers.unused || 0).toLocaleString("fr-FR") }} ·<br />
              {{ $t("Expire ce mois") }}:
              {{ (dashboard?.vouchers_expiring_this_month || 0).toLocaleString("fr-FR") }}
            </div>
          </div>
          <div class="kpi-right" :class="{ 'kpi-right-mobile': $vuetify.breakpoint.xs }">
            <div class="donut-wrap" :class="{ 'donut-wrap-mobile': $vuetify.breakpoint.xs }">
              <apexchart
                :key="'donut-vouchers-' + chartKey"
                type="donut"
                :height="$vuetify.breakpoint.xs ? 80 : 110"
                :options="vouchersOptions"
                :series="vouchers.series"
              />
              <div class="donut-center" :class="{ 'donut-center-mobile': $vuetify.breakpoint.xs }">
                {{ centerLabel("vouchers", vouchers.total) }}
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <!-- KPI 3 : Montant des transactions -->
      <v-col cols="12" sm="6" md="4">
        <div
          class="kpi-card bgTable kpi-clickable"
          :class="{ 'kpi-card-mobile': $vuetify.breakpoint.xs }"
          @click="$router.push({ name: 'TransactionDetail' })"
        >
          <div class="kpi-left">
            <div class="kpi-title" :class="{ 'kpi-title-mobile': $vuetify.breakpoint.xs }">
              {{ $t("Transactions") }}
            </div>
            <div class="kpi-value" :class="{ 'kpi-value-mobile': $vuetify.breakpoint.xs }">
              {{ formatFcfa(dashboard?.total_expense) }}
            </div>
            <div class="kpi-sub">{{ $t("Conso du mois encours") }}</div>
          </div>
          <div class="kpi-right" :class="{ 'kpi-right-mobile': $vuetify.breakpoint.xs }">
            <apexchart
              :key="'sparkline-' + chartKey"
              type="area"
              :height="$vuetify.breakpoint.xs ? 80 : 120"
              :options="sparklineAreaOptions"
              :series="sparklineSeries"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ========== SECTION YTD (masquée sur mobile) ========== -->
    <v-row class="mb-2" v-if="$vuetify.breakpoint.mdAndUp">
      <v-col cols="12">
        <div class="resume-container-home bgTable">
          <v-tabs v-model="ytdTab" background-color="transparent" slider-color="primary">
            <v-tab><v-icon left small>mdi-office-building</v-icon>{{ $t("Par département") }}</v-tab>
            <v-tab><v-icon left small>mdi-car</v-icon>{{ $t("Par véhicule") }}</v-tab>
            <v-tab><v-icon left small>mdi-chart-line</v-icon>{{ $t("Tendance mensuelle") }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="ytdTab">
            <!-- Tab 1: Départements -->
            <v-tab-item>
              <v-card flat class="pa-4">
                <!-- Filtres locaux -->
                <v-row class="mb-2">
                  <v-col cols="12" class="d-flex justify-end align-center flex-wrap" style="gap: 8px;">
                    <v-select
                      dense
                      hide-details
                      outlined
                      style="max-width: 200px"
                      :items="ytdItems"
                      v-model="ytdDeptPeriod"
                      @change="onDeptPeriodChange"
                      :label="$t('Période')"
                    />
                    <template v-if="ytdDeptPeriod === 'range'">
                      <v-menu
                        v-model="ytdDeptStartMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            dense
                            outlined
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                            :label="$t('Début')"
                            v-model="ytdDeptStart"
                            readonly
                            style="max-width: 160px"
                          />
                        </template>
                        <v-date-picker v-model="ytdDeptStart" @input="ytdDeptStartMenu = false" />
                      </v-menu>
                      <v-menu
                        v-model="ytdDeptEndMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            dense
                            outlined
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                            :label="$t('Fin')"
                            v-model="ytdDeptEnd"
                            readonly
                            style="max-width: 160px"
                          />
                        </template>
                        <v-date-picker v-model="ytdDeptEnd" @input="ytdDeptEndMenu = false" />
                      </v-menu>
                      <v-btn
                        small
                        color="primary"
                        @click="applyDeptRange"
                        :loading="loading"
                      >{{ $t("Appliquer") }}</v-btn>
                    </template>
                  </v-col>
                </v-row>

                <v-row v-if="ytdDepartments.length > 0">
                  <!-- TABLEAU -->
                  <v-col cols="12" md="6">
                    <v-simple-table dense class="ytd-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t("Département") }}</th>
                            <th class="text-right">{{ $t("Consommation") }}</th>
                            <th class="text-right">{{ deptBudgetColumnLabel }}</th>
                            <th class="text-right">{{ $t("Taux") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="dept in ytdDepartments" :key="dept.department_id">
                            <td>
                              <div class="font-weight-medium">{{ dept.department_name }}</div>
                              <div class="text-caption grey--text">
                                {{ formatBudgetFrequency(dept.budget_frequency) }}
                                <span
                                  v-if="dept.period_label"
                                  class="primary--text"
                                >
                                  → {{ dept.period_label }}
                                </span>
                              </div>
                            </td>
                            <td class="text-right font-weight-bold">
                              {{ toFR(dept.ytd_amount) }} XOF
                            </td>
                            <td class="text-right">
                              {{ dept.budget_for_period != null ? toFR(dept.budget_for_period) + ' XOF' : "N/A" }}
                            </td>
                            <td class="text-right">
                              <v-chip
                                v-if="dept.consumption_rate !== null"
                                small
                                :color="getConsumptionColor(dept.consumption_rate)"
                                text-color="white"
                              >{{ dept.consumption_rate }}%</v-chip>
                              <span v-else class="grey--text">N/A</span>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                  <!-- GRAPH camembert -->
                  <v-col cols="12" md="6">
                    <apexchart
                      ref="deptPieChart"
                      :key="'dept-pie-' + ytdDeptChartKey"
                      type="pie"
                      :height="$vuetify.breakpoint.smAndDown ? 300 : 380"
                      :options="ytdDeptPieOptions"
                      :series="ytdDeptPieSeries"
                    />
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col cols="12">
                    <v-alert type="info" color="secondary" text>
                      {{ $t("Aucune donnée YTD disponible pour les départements.") }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- Tab 2: Véhicules -->
            <v-tab-item>
              <v-card flat class="pa-4">
                <!-- Filtres locaux -->
                <v-row class="mb-2">
                  <v-col cols="12" class="d-flex justify-end align-center flex-wrap" style="gap: 8px;">
                    <v-select
                      dense
                      hide-details
                      outlined
                      style="max-width: 200px"
                      :items="ytdItems"
                      v-model="ytdVehPeriod"
                      @change="onVehPeriodChange"
                      :label="$t('Période')"
                    />
                    <template v-if="ytdVehPeriod === 'range'">
                      <v-menu
                        v-model="ytdVehStartMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            dense
                            outlined
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                            :label="$t('Début')"
                            v-model="ytdVehStart"
                            readonly
                            style="max-width: 160px"
                          />
                        </template>
                        <v-date-picker v-model="ytdVehStart" @input="ytdVehStartMenu = false" />
                      </v-menu>
                      <v-menu
                        v-model="ytdVehEndMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            dense
                            outlined
                            hide-details
                            v-bind="attrs"
                            v-on="on"
                            :label="$t('Fin')"
                            v-model="ytdVehEnd"
                            readonly
                            style="max-width: 160px"
                          />
                        </template>
                        <v-date-picker v-model="ytdVehEnd" @input="ytdVehEndMenu = false" />
                      </v-menu>
                      <v-btn
                        small
                        color="primary"
                        @click="applyVehRange"
                        :loading="loading"
                      >{{ $t("Appliquer") }}</v-btn>
                    </template>
                  </v-col>
                </v-row>

                <v-row v-if="ytdVehicles.length > 0">
                  <!-- TABLEAU -->
                  <v-col cols="12" md="5">
                    <v-simple-table dense class="ytd-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>{{ $t("Véhicule / Conducteur") }}</th>
                            <th class="text-right">{{ $t("Consommation") }}</th>
                            <th class="text-right">{{ vehBudgetColumnLabel }}</th>
                            <th class="text-right">{{ $t("Taux") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="veh in ytdVehicles" :key="veh.vehicle_id">
                            <td>
                              <div class="font-weight-medium">
                                {{ veh.driver_name || veh.registration_number }}
                              </div>
                              <div class="text-caption grey--text">
                                {{ veh.driver_name ? veh.registration_number : (veh.department_name || "N/A") }}
                              </div>
                            </td>
                            <td class="text-right font-weight-bold">
                              {{ toFR(veh.ytd_amount) }} XOF
                            </td>
                            <td class="text-right">
                              {{ veh.budget_for_period != null ? toFR(veh.budget_for_period) + ' XOF' : "N/A" }}
                            </td>
                            <td class="text-right">
                              <v-chip
                                v-if="veh.consumption_rate !== null"
                                small
                                :color="getConsumptionColor(veh.consumption_rate)"
                                text-color="white"
                              >{{ veh.consumption_rate }}%</v-chip>
                              <span v-else class="grey--text">N/A</span>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                  <!-- GRAPH -->
                  <v-col cols="12" md="7">
                    <apexchart
                      ref="vehBarChart"
                      :key="'veh-bar-' + ytdVehChartKey"
                      type="bar"
                      :height="ytdVehicleChartHeight"
                      :options="ytdVehicleOptions"
                      :series="ytdVehicleSeries"
                    />
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col cols="12">
                    <v-alert type="info" text>
                      {{ $t("Aucune donnée YTD disponible pour les véhicules.") }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- Tab 3: Tendance mensuelle -->
            <v-tab-item>
              <v-card flat class="pa-4">
                <v-row v-if="ytdMonthlyTrend.length > 0">
                  <v-col cols="12" class="d-flex justify-end">
                    <v-btn-toggle v-model="trendMetric" dense>
                      <v-btn value="amount">{{ $t("Montant") }}</v-btn>
                      <v-btn value="liters">{{ $t("Litres") }}</v-btn>
                    </v-btn-toggle>
                  </v-col>
                  <v-col cols="12">
                    <apexchart
                      ref="trendLineChart"
                      :key="'trend-line-' + trendChartKey"
                      type="line"
                      height="350"
                      :options="ytdMonthlyOptions"
                      :series="ytdMonthlySeries"
                    />
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col cols="12">
                    <v-alert type="info" text>
                      {{ $t("Aucune donnée de tendance mensuelle disponible.") }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-col>
    </v-row>

    <!-- Graphiques Business (masqués sur mobile) -->
    <v-row v-if="company.company_type == 'Business' && !department && $vuetify.breakpoint.mdAndUp">
      <v-col cols="12" :md="$vuetify.breakpoint.smAndDown ? 12 : 8" v-if="locationDataTop5.length">
        <div class="resume-container-home bgTable">
          <apexchart
            :key="'location-bar-' + chartKey"
            type="bar"
            :height="mobileChartHeight"
            :options="locationOptionsTop5"
            :series="locationSeriesTop5"
          />
        </div>
      </v-col>
      <v-col cols="12" :md="$vuetify.breakpoint.smAndDown ? 12 : 4" v-if="productData.length">
        <div class="resume-container-home bgTable">
          <apexchart
            :key="'product-pie-' + chartKey"
            type="pie"
            :height="$vuetify.breakpoint.smAndDown ? 300 : 318"
            :options="productPieOptions"
            :series="productPieSeries"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { BusinessService } from "@/services"
import { getAssets } from "@/plugins/vuetify"

if (typeof window !== "undefined") {
  window.Apex = window.Apex || {}
  window.Apex.chart = Object.assign({}, window.Apex.chart, {
    redrawOnWindowResize: true,
    redrawOnParentResize: true,
    animations: { enabled: false },
  })
}

export default {
  name: "Home",
  components: {
    apexchart: () => import("vue-apexcharts"),
  },
  data() {
    return {
      dashboard: {},
      dateFilterMenu: false,
      centerByKey: { vehicles: null, vouchers: null },
      loading: false,
      chartKey: 0,
      ytdDeptChartKey: 0,
      ytdVehChartKey: 0,
      trendChartKey: 0,
      filters: {
        start_date: null,
        end_date: null,
        department: null,
        ytd_dept_period: "year",
        ytd_dept_start: null,
        ytd_dept_end: null,
        ytd_veh_period: "year",
        ytd_veh_start: null,
        ytd_veh_end: null,
      },
      ytdTab: 0,
      ytdItems: [
        { text: "Année en cours", value: "year" },
        { text: "Mois en cours", value: "month" },
        { text: "Intervalle", value: "range" },
      ],
      ytdDeptPeriod: "year",
      ytdDeptStart: null,
      ytdDeptEnd: null,
      ytdDeptStartMenu: false,
      ytdDeptEndMenu: false,
      ytdVehPeriod: "year",
      ytdVehStart: null,
      ytdVehEnd: null,
      ytdVehStartMenu: false,
      ytdVehEndMenu: false,
      trendMetric: "amount",
    }
  },

  computed: {
    department() {
      return this.$store.state.app.project
    },
    assets() {
      return getAssets(this)
    },
    company() {
      return this.$store.state.app.company
    },

    // Label dynamique pour la colonne budget départements
    deptBudgetColumnLabel() {
      if (this.ytdDeptPeriod === "month") {
        return this.$t("Budget mensuel")
      } else if (this.ytdDeptPeriod === "range") {
        const months = this.ytdDepartments[0]?.months_in_period || 1
        return this.$t("Budget") + ` (${months} mois)`
      }
      return this.$t("Budget annuel")
    },

    // Label dynamique pour la colonne budget véhicules
    vehBudgetColumnLabel() {
      if (this.ytdVehPeriod === "month") {
        return this.$t("Limite mensuelle")
      } else if (this.ytdVehPeriod === "range") {
        const months = this.ytdVehicles[0]?.months_in_period || 1
        return this.$t("Limite") + ` (${months} mois)`
      }
      return this.$t("Limite annuelle")
    },

    vehiclesActive() {
      return Number(this.dashboard?.vehicles_breakdown?.active || 0)
    },
    vehiclesBlocked() {
      return Number(this.dashboard?.vehicles_breakdown?.blocked || 0)
    },
    vehicles() {
      const total = this.vehiclesActive + this.vehiclesBlocked
      const items = [
        { key: "active", label: this.$t("Actifs"), value: this.vehiclesActive, color: "#16a34a" },
        { key: "blocked", label: this.$t("Bloqués"), value: this.vehiclesBlocked, color: "#ef4444" },
      ]
      return this.buildDonut(total, items)
    },
    vehiclesOptions() {
      return this.makeDonutOptions("vehicles", this.vehicles.labels, this.vehicles.colors)
    },

    vouchers() {
      const br = this.dashboard?.voucher_breakdown_simple || {}
      const inUse = Number(br.in_use || 0)
      const unused = Number(br.unused || 0)
      const fallbackTotal = Number(this.dashboard?.voucher_kpi || 0)
      const total = (inUse + unused) > 0 ? (inUse + unused) : fallbackTotal
      const items = [
        { key: "in_use", label: this.$t("En cours"), value: inUse, color: "#0ea5e9" },
        { key: "unused", label: this.$t("Non utilisé"), value: unused, color: "#10b981" },
      ]
      const donut = this.buildDonut(total, items)
      return { ...donut, in_use: inUse, unused, total }
    },
    vouchersOptions() {
      return this.makeDonutOptions("vouchers", this.vouchers.labels, this.vouchers.colors)
    },

    last10Days() {
      return this.dashboard?.consumption_last_10_days || []
    },
    sparklineSeries() {
      return [{
        name: this.$t("Consommation"),
        data: this.last10Days.map(d => this.round0(d.total || 0)),
      }]
    },
    sparklineAreaOptions() {
      return {
        chart: {
          sparkline: { enabled: true },
          animations: { enabled: true },
          toolbar: { show: false }
        },
        stroke: { width: 2, curve: "smooth" },
        dataLabels: { enabled: false },
        fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        xaxis: { categories: this.last10Days.map(d => d.date) },
        tooltip: {
          x: { show: true },
          y: { formatter: v => `${this.toFR(v)} XOF` },
        },
      }
    },

    ytdDepartments() {
      const src = this.dashboard?.ytd_departments || []
      return src.map(d => ({
        department_id: d.department_id,
        department_name: d.department_name,
        ytd_amount: this.round0(d.ytd_amount),
        ytd_liters: this.round0(d.ytd_liters),
        budget_for_period: d.budget_for_period != null ? this.round0(d.budget_for_period) : null,
        budget_annual: d.budget_annual != null ? this.round0(d.budget_annual) : null,
        budget_frequency: d.budget_period || null,
        consumption_rate: d.consumption_rate != null ? this.round0(d.consumption_rate) : null,
        period_label: d.period_label || null,
        period_mode: d.period_mode || "year",
        months_in_period: d.months_in_period || 12,
      }))
    },

    ytdDeptPieData() {
      const all = (this.ytdDepartments || [])
        .slice()
        .sort((a, b) => (b.ytd_amount || 0) - (a.ytd_amount || 0))
      const top = all.slice(0, 4)
      const rest = all.slice(4)
      const otherSum = rest.reduce((s, d) => s + (d.ytd_amount || 0), 0)
      const labels = top.map(d => d.department_name || this.$t("N/A"))
      const series = top.map(d => this.round0(d.ytd_amount || 0))
      if (otherSum > 0) {
        labels.push(this.$t("Autres"))
        series.push(this.round0(otherSum))
      }
      return { labels, series }
    },
    ytdDeptPieOptions() {
      const labels = this.ytdDeptPieData.labels
      const periodLabel = this.ytdDeptPeriod === "month" 
        ? this.$t("(mois en cours)") 
        : this.ytdDeptPeriod === "range" 
          ? this.$t("(période sélectionnée)")
          : this.$t("(année en cours)")
      return {
        chart: { type: "pie", toolbar: { show: false } },
        labels,
        colors: ["#2563eb", "#ef4444", "#22c55e", "#f59e0b", "#6b7280"],
        dataLabels: {
          enabled: true,
          formatter: (val) => `${this.round0(val)}%`,
          style: { fontSize: this.$vuetify.breakpoint.xs ? "10px" : "12px" },
        },
        legend: { position: "bottom", fontSize: this.$vuetify.breakpoint.xs ? "11px" : "13px" },
        tooltip: { y: { formatter: v => `${this.toFR(v)} XOF` } },
        title: {
          text: `${this.$t("Répartition par département")} ${periodLabel}`,
          style: { fontSize: this.$vuetify.breakpoint.xs ? "14px" : "16px" },
        },
      }
    },
    ytdDeptPieSeries() {
      return this.ytdDeptPieData.series
    },

    ytdVehicles() {
      const src = this.dashboard?.ytd_vehicles || []
      return src.map(v => ({
        vehicle_id: v.vehicle_id,
        registration_number: v.registration_number,
        driver_name: v.driver_name,
        department_name: v.department_name,
        ytd_amount: this.round0(v.ytd_amount),
        ytd_liters: this.round0(v.ytd_liters),
        transaction_count: v.transaction_count || 0,
        monthly_limit: v.monthly_limit != null ? this.round0(v.monthly_limit) : null,
        budget_for_period: v.budget_for_period != null ? this.round0(v.budget_for_period) : null,
        consumption_rate: v.consumption_rate != null ? this.round0(v.consumption_rate) : null,
        period_mode: v.period_mode || "year",
        months_in_period: v.months_in_period || 12,
      }))
    },
    ytdVehicleChartHeight() {
      const n = this.ytdVehicles.length || 1
      return Math.max(250, Math.min(n * 50, 500))
    },
    ytdVehicleOptions() {
      const isMobile = this.$vuetify.breakpoint.smAndDown
      const periodLabel = this.ytdVehPeriod === "month" 
        ? this.$t("(mois en cours)") 
        : this.ytdVehPeriod === "range" 
          ? this.$t("(période sélectionnée)")
          : this.$t("(année en cours)")
      return {
        chart: { type: "bar", toolbar: { show: false } },
        title: {
          text: `${this.$t("Top véhicules consommateurs")} ${periodLabel}`,
          style: { fontSize: isMobile ? "14px" : "18px", fontWeight: 600 },
        },
        plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 8, barHeight: "70%" } },
        xaxis: { labels: { formatter: val => this.toFR(val) } },
        dataLabels: {
          enabled: !isMobile,
          formatter: val => `${this.toFR(val)} XOF`,
          style: { fontSize: "11px", fontWeight: 600 },
        },
        colors: ["#dc2626", "#ea580c", "#f59e0b", "#eab308", "#84cc16", "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9"],
        tooltip: { y: { formatter: val => `${this.toFR(val)} XOF` } },
        legend: { show: false },
        yaxis: {
          labels: {
            formatter: (val, opts) => {
              const lbls = opts?.w?.globals?.labels || []
              return lbls[opts?.i] || val
            },
          },
        },
      }
    },
    ytdVehicleSeries() {
      const points = this.ytdVehicles.map(v => ({
        x: String(v.driver_name || v.registration_number || "—"),
        y: this.round0(v.ytd_amount || 0),
      }))
      return [{ name: this.$t("Consommation"), data: points }]
    },

    ytdMonthlyTrend() {
      return this.dashboard?.ytd_monthly_trend || []
    },
    frenchMonths() {
      return ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
    },
    ytdMonthlyOptions() {
      const labels = this.ytdMonthlyTrend.map(m =>
        m.month_name || `${this.frenchMonths[(m.month_index || 1) - 1]} ${m.year}`
      )
      const isAmount = this.trendMetric === "amount"
      return {
        chart: { type: "line", toolbar: { show: true }, zoom: { enabled: false } },
        title: { text: this.$t("Évolution mensuelle (Jan–Déc)"), style: { fontSize: "18px", fontWeight: 600 } },
        xaxis: { categories: labels, labels: { rotate: -45 } },
        yaxis: { labels: { formatter: v => this.toFR(v) } },
        stroke: { width: 4, curve: "smooth" },
        markers: { size: 6, hover: { size: 8 } },
        colors: [isAmount ? "#1d4ed8" : "#f97316"],
        dataLabels: { enabled: false },
        tooltip: {
          shared: false,
          intersect: false,
          y: {
            formatter: v => isAmount ? `${this.toFR(v)} XOF` : `${this.toFR(v)} L`,
          },
        },
        legend: { show: false },
        grid: { borderColor: "#e0e0e0", strokeDashArray: 4 },
      }
    },
    ytdMonthlySeries() {
      if (this.trendMetric === "amount") {
        return [{ name: this.$t("Montant"), data: this.ytdMonthlyTrend.map(m => this.round0(m.total_amount)) }]
      }
      return [{ name: this.$t("Litres"), data: this.ytdMonthlyTrend.map(m => this.round0(m.total_liters)) }]
    },

    deptData() {
      const arr = this.dashboard?.department_expense || []
      return arr
        .map(d => ({ name: d?.department_name || "N/A", total: this.round0(d?.total) }))
        .filter(d => d.total > 0)
    },
    productData() {
      const arr = this.dashboard?.product_sales || []
      return arr
        .map(p => ({ name: p?.["product__name"] || "N/A", total: this.round0(p?.total) }))
        .filter(p => p.total > 0)
    },
    locationDataTop5() {
      const arr = this.dashboard?.location_sales || []
      return arr.slice(0, 5).map(l => ({
        name: l?.["staffMembers__location_shop__name"] || "N/A",
        total: this.round0(l?.total),
      }))
    },

    productPieOptions() {
      const labels = this.productData.map(p => p.name)
      const fallback = ["#4b5563", "#3b82f6", "#ef4444", "#22c55e", "#a855f7", "#06b6d4", "#e11d48", "#84cc16", "#f97316", "#0ea5e9"]
      const colors = labels.map((name, i) => {
        const n = (name || "").toString().trim().toLowerCase()
        if (n.includes("super")) return "#16a34a"
        if (n.includes("gasoil") || n.includes("diesel")) return "#f59e0b"
        return fallback[i % fallback.length]
      })
      return {
        chart: { type: "pie" },
        labels,
        colors,
        title: { text: this.$t("Ventes par produit"), style: { fontSize: this.$vuetify.breakpoint.xs ? "14px" : "16px" } },
        legend: { position: "bottom", fontSize: this.$vuetify.breakpoint.xs ? "11px" : "13px" },
        dataLabels: {
          enabled: true,
          formatter: val => `${this.round0(val)}%`,
          dropShadow: { enabled: false },
          style: { fontSize: this.$vuetify.breakpoint.xs ? "10px" : "12px" },
        },
        tooltip: { y: { formatter: v => `${this.toFR(v)} XOF` } },
      }
    },
    productPieSeries() {
      return this.productData.map(p => p.total)
    },
    mobileChartHeight() {
      if (this.$vuetify.breakpoint.xs) return 250
      if (this.$vuetify.breakpoint.smAndDown) return 280
      return this.locationChartHeightTop5
    },
    locationChartHeightTop5() {
      const n = this.locationDataTop5.length || 1
      return Math.min(220 + n * 36, 300)
    },
    locationOptionsTop5() {
      const names = this.locationDataTop5.map(l => l.name)
      const isMobile = this.$vuetify.breakpoint.smAndDown
      return {
        chart: { id: "location-sales-top5", type: "bar", toolbar: { show: false } },
        title: { text: this.$t("Top 5 ventes par station"), style: { fontSize: isMobile ? "14px" : "16px" } },
        plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 6, barHeight: "60%" } },
        yaxis: { categories: names },
        xaxis: {
          labels: {
            style: { fontSize: isMobile ? "11px" : "13px", fontWeight: 600 },
            formatter: val => this.toFR(val),
          },
        },
        dataLabels: {
          enabled: !isMobile,
          style: { fontSize: "12px", fontWeight: 700 },
          formatter: val => `${this.toFR(val)}`,
        },
        stroke: { show: true, width: 2 },
        fill: { opacity: 0.95 },
        grid: { strokeDashArray: 3 },
        colors: ["#2563eb", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#06b6d4", "#e11d48", "#84cc16", "#a855f7", "#10b981", "#f97316", "#3b82f6"],
        tooltip: { y: { formatter: val => `${this.toFR(val)} XOF` } },
        legend: { show: false },
      }
    },
    locationSeriesTop5() {
      return [{ name: this.$t("Montant"), data: this.locationDataTop5.map(l => l.total) }]
    },
  },

  created() {
    this.initYtdItems()
    this.loadDashboard()
  },

  watch: {
    department() {
      this.loadDashboard()
    },
    ytdTab(newVal) {
      this.$nextTick(() => {
        this.refreshChartsForTab(newVal)
      })
    },
    trendMetric() {
      this.trendChartKey++
    },
  },

  methods: {
    initYtdItems() {
      this.ytdItems = [
        { text: this.$t("Année en cours"), value: "year" },
        { text: this.$t("Mois en cours"), value: "month" },
        { text: this.$t("Intervalle"), value: "range" },
      ]
    },

    round0(v) {
      const n = Number(v || 0)
      return Math.round(n)
    },

    toFR(v) {
      return this.round0(v).toLocaleString("fr-FR")
    },

    centerLabel(key, total) {
      const v = this.centerByKey?.[key]
      return v != null ? v : this.toFR(total)
    },

    buildDonut(total, items) {
      const filtered = (items || []).filter(i => Number(i.value) > 0)
      const get = k => Number((items.find(i => i.key === k) || {}).value || 0)
      return {
        total: Number(total || 0),
        active: get("active"),
        blocked: get("blocked"),
        labels: filtered.map(i => i.label),
        series: filtered.map(i => Number(i.value)),
        colors: filtered.map(i => i.color),
      }
    },

    makeDonutOptions(key, labels, colors) {
      const vm = this
      const localLabels = Array.isArray(labels) ? labels.slice() : []

      function bindDomHover(ctx) {
        try {
          const series = ctx?.w?.config?.series || []
          const nodes = ctx?.el?.querySelectorAll?.(".apexcharts-pie-series path") || []
          for (let i = 0; i < nodes.length; i++) {
            (function (index) {
              const node = nodes[index]
              if (node.__hkBound) {
                node.removeEventListener("mouseenter", node.__hkEnter)
                node.removeEventListener("mouseleave", node.__hkLeave)
              }
              const onEnter = function () {
                vm.centerByKey = { ...(vm.centerByKey || {}), [key]: vm.toFR(series[index] || 0) }
              }
              const onLeave = function () {
                vm.centerByKey = { ...(vm.centerByKey || {}), [key]: null }
              }
              node.addEventListener("mouseenter", onEnter)
              node.addEventListener("mouseleave", onLeave)
              node.__hkBound = true
              node.__hkEnter = onEnter
              node.__hkLeave = onLeave
            })(i)
          }
        } catch (e) {
          console.error(e)
        }
      }

      return {
        chart: {
          sparkline: { enabled: true },
          animations: { enabled: false },
          toolbar: { show: false },
          events: { mounted: bindDomHover, updated: bindDomHover },
        },
        labels: localLabels,
        colors,
        legend: { show: false },
        dataLabels: { enabled: false },
        tooltip: {
          shared: false,
          intersect: true,
          x: {
            formatter: (val, opts) => {
              const i = Number.isInteger(opts?.seriesIndex) ? opts.seriesIndex : -1
              return i >= 0 ? (localLabels[i] || val) : ""
            },
          },
          y: { formatter: val => vm.toFR(val) },
        },
        plotOptions: { pie: { expandOnClick: false, donut: { size: "70%", labels: { show: false } } } },
        states: { hover: { filter: { type: "none" } }, active: { filter: { type: "none" } } },
      }
    },

    getConsumptionColor(rate) {
      const n = Number(rate)
      if (!isFinite(n)) return "grey"
      if (n < 60) return "success"
      if (n < 85) return "warning"
      if (n <= 100) return "error"
      return "deep-purple accent-3"
    },

    formatBudgetFrequency(freq) {
      const mapping = {
        Annual: "Annuel",
        "Semi-annual": "Semestriel",
        Quarterly: "Trimestriel",
        Monthly: "Mensuel",
        Weekly: "Hebdomadaire",
        Daily: "Quotidien",
        Single: "Unique",
      }
      return mapping[freq] || freq || "N/A"
    },

    getMultiplier(freq) {
      const mapping = { Annual: 1, "Semi-annual": 2, Quarterly: 4, Monthly: 12, Weekly: 52, Daily: 365, Single: 1 }
      return mapping[freq] || 1
    },

    refreshChartsForTab(tabIndex) {
      if (tabIndex === 0) {
        this.ytdDeptChartKey++
      } else if (tabIndex === 1) {
        this.ytdVehChartKey++
      } else if (tabIndex === 2) {
        this.trendChartKey++
      }
    },

    applyDateFilter() {
      this.dateFilterMenu = false
      this.loadDashboard()
    },

    resetDates() {
      this.filters.start_date = null
      this.filters.end_date = null
      this.loadDashboard()
    },

    onDeptPeriodChange() {
      this.filters.ytd_dept_period = this.ytdDeptPeriod
      if (this.ytdDeptPeriod !== "range") {
        this.ytdDeptStart = null
        this.ytdDeptEnd = null
        this.filters.ytd_dept_start = null
        this.filters.ytd_dept_end = null
        this.loadDashboard()
      }
    },

    applyDeptRange() {
      if (!this.ytdDeptStart || !this.ytdDeptEnd) {
        return
      }
      this.filters.ytd_dept_period = "range"
      this.filters.ytd_dept_start = this.ytdDeptStart
      this.filters.ytd_dept_end = this.ytdDeptEnd
      this.loadDashboard()
    },

    onVehPeriodChange() {
      this.filters.ytd_veh_period = this.ytdVehPeriod
      if (this.ytdVehPeriod !== "range") {
        this.ytdVehStart = null
        this.ytdVehEnd = null
        this.filters.ytd_veh_start = null
        this.filters.ytd_veh_end = null
        this.loadDashboard()
      }
    },

    applyVehRange() {
      if (!this.ytdVehStart || !this.ytdVehEnd) {
        return
      }
      this.filters.ytd_veh_period = "range"
      this.filters.ytd_veh_start = this.ytdVehStart
      this.filters.ytd_veh_end = this.ytdVehEnd
      this.loadDashboard()
    },

    loadDashboard() {
      this.loading = true
      this.filters.department = this.department?.id || null

      this.filters.ytd_dept_period = this.ytdDeptPeriod
      this.filters.ytd_dept_start = this.ytdDeptStart
      this.filters.ytd_dept_end = this.ytdDeptEnd
      this.filters.ytd_veh_period = this.ytdVehPeriod
      this.filters.ytd_veh_start = this.ytdVehStart
      this.filters.ytd_veh_end = this.ytdVehEnd

      BusinessService.getHome(this.filters)
        .then(result => {
          this.dashboard = result || {}
          this.chartKey++
          this.ytdDeptChartKey++
          this.ytdVehChartKey++
          this.trendChartKey++
        })
        .finally(() => {
          this.loading = false
        })
    },

    formatFcfa(v) {
      return `${this.toFR(v)} XOF`
    },
  },
}
</script>

<style scoped>
.kpi-card {
  cursor: pointer;
}
.kpi-clickable {
  cursor: pointer;
}
.resume-container-home {
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.kpi-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
  border-radius: 14px;
  padding: 16px 16px 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  border: 1px solid rgba(16, 24, 40, 0.06);
  min-height: 136px;
  flex-direction: row;
  flex-wrap: nowrap;
}
.kpi-card-mobile {
  min-height: 110px;
  padding: 12px;
  gap: 8px;
}
.kpi-left {
  flex: 1;
  min-width: 0;
}
.kpi-right {
  width: 150px;
  min-width: 140px;
  display: flex;
  justify-content: center;
}
.kpi-right-mobile {
  width: 100px;
  min-width: 90px;
}
.kpi-title {
  font-weight: 600;
  font-size: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi-title-mobile {
  font-size: 14px;
}
.kpi-value {
  font-weight: 800;
  font-size: 28px;
  line-height: 1.1;
  margin-top: 2px;
}
.kpi-value-mobile {
  font-size: 20px;
}
.kpi-sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.donut-wrap {
  position: relative;
  width: 110px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.donut-wrap-mobile {
  width: 80px;
  min-height: 90px;
}
.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  pointer-events: none;
}
.donut-center-mobile {
  font-size: 11px;
}

.ytd-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.ytd-table thead tr {
  background-color: #f5f5f5;
}
.ytd-table tbody tr:hover {
  background-color: #fafafa;
}

@media (max-width: 960px) {
  .container--fluid {
    padding: 8px !important;
  }
}

@media (max-width: 600px) {
  .kpi-card {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    align-items: center !important;
    gap: 8px !important;
  }
  .kpi-left,
  .kpi-right {
    flex: none !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }
  .kpi-right,
  .kpi-right-mobile {
    justify-content: center !important;
  }
  .donut-wrap {
    width: 100% !important;
    min-height: 90px !important;
  }
  .kpi-title {
    font-size: 14px !important;
  }
  .kpi-value {
    font-size: 20px !important;
  }
}
</style>